import {
  UtmParameters,
  UtmData,
  StorageOptions,
  UTM_PARAMETER_KEYS,
  UTM_STORAGE_KEYS,
  UTM_COOKIE_PREFIX,
  UTM_DB_NAME,
  UTM_DB_VERSION,
  UTM_STORE_NAME,
  DEFAULT_COOKIE_EXPIRY_DAYS,
  IndexedDBUtmRecord,
} from './utm-types'
import {
  getFromIndexedDB,
  setInIndexedDB,
  deleteFromIndexedDB,
  IndexedDBConfig,
} from '../indexed-db'
import {
  setCookie,
  getCookiesByPrefix,
  deleteCookiesByPrefix,
} from '../cookies'
import { localStorageUtils, sessionStorageUtils } from '../storage'
import { getProfileInfo } from '../../services/profile'

// IndexedDB configuration for UTM data
const UTM_DB_CONFIG: IndexedDBConfig = {
  dbName: UTM_DB_NAME,
  version: UTM_DB_VERSION,
  storeName: UTM_STORE_NAME,
  keyPath: 'id',
}

export const extractUtmParametersFromUrl = (
  search: string,
): Partial<UtmParameters> => {
  const parameters: Partial<UtmParameters> = {}

  try {
    const urlParams = new URLSearchParams(search)

    UTM_PARAMETER_KEYS.forEach(key => {
      const value = urlParams.get(key)
      if (value && value.trim()) {
        parameters[key] = value.trim()
      }
    })
  } catch (error) {
    console.error('Error extracting UTM parameters from URL:', error)
  }

  return parameters
}

export const saveUtmToSessionStorage = async (
  parameters: Partial<UtmParameters>,
): Promise<void> => {
  try {
    const timestamp = Date.now()

    const utmData: UtmData = {
      parameters: parameters as UtmParameters,
      timestamp,
      source: 'sessionStorage',
    }

    sessionStorageUtils.set(UTM_STORAGE_KEYS.UTM_DATA, utmData)
  } catch (error) {
    console.error('Error saving UTM to session storage:', error)
  }
}

export const getUtmFromSessionStorage = (): UtmData | null => {
  try {
    return sessionStorageUtils.get<UtmData | null>(
      UTM_STORAGE_KEYS.UTM_DATA,
      null,
    )
  } catch (error) {
    console.error('Error getting UTM from session storage:', error)
    return null
  }
}

export const saveUtmToLocalStorage = async (
  parameters: Partial<UtmParameters>,
): Promise<void> => {
  try {
    const timestamp = Date.now()

    const utmData: UtmData = {
      parameters: parameters as UtmParameters,
      timestamp,
      source: 'localStorage',
    }

    localStorageUtils.set(UTM_STORAGE_KEYS.UTM_DATA, utmData)
  } catch (error) {
    console.error('Error saving UTM to local storage:', error)
  }
}

/**
 * Get UTM parameteFrs from local storage
 */
export const getUtmFromLocalStorage = (): UtmData | null => {
  try {
    return localStorageUtils.get<UtmData | null>(
      UTM_STORAGE_KEYS.UTM_DATA,
      null,
    )
  } catch (error) {
    console.error('Error getting UTM from local storage:', error)
    return null
  }
}

export const saveUtmToIndexedDB = async (
  parameters: Partial<UtmParameters>,
): Promise<void> => {
  try {
    const timestamp = Date.now()

    const existingRecord = await getFromIndexedDB<IndexedDBUtmRecord>(
      UTM_DB_CONFIG,
      'utm-data',
    )

    const record: IndexedDBUtmRecord = {
      parameters: {
        ...existingRecord?.parameters,
        ...parameters,
      } as UtmParameters,
      timestamp: existingRecord?.timestamp || timestamp,
      lastUpdated: timestamp,
    }

    await setInIndexedDB(UTM_DB_CONFIG, 'utm-data', record)
  } catch (error) {
    console.error('Error saving UTM to IndexedDB:', error)
  }
}

export const getUtmFromIndexedDB = async (): Promise<UtmData | null> => {
  try {
    const record = await getFromIndexedDB<IndexedDBUtmRecord>(
      UTM_DB_CONFIG,
      'utm-data',
    )

    if (!record) return null

    return {
      parameters: record.parameters,
      timestamp: record.timestamp,
      source: 'indexeddb',
    }
  } catch (error) {
    console.error('Error getting UTM from IndexedDB:', error)
    return null
  }
}

export const saveUtmToCookies = async (
  parameters: Partial<UtmParameters>,
  expireDays: number = DEFAULT_COOKIE_EXPIRY_DAYS,
): Promise<void> => {
  try {
    const timestamp = Date.now()

    UTM_PARAMETER_KEYS.forEach(key => {
      const value = parameters[key]
      if (value) {
        setCookie(`${UTM_COOKIE_PREFIX}${key}`, value, {
          expires: expireDays,
          sameSite: 'Lax',
        })
      }
    })

    setCookie(`${UTM_COOKIE_PREFIX}timestamp`, timestamp.toString(), {
      expires: expireDays,
      sameSite: 'Lax',
    })
    setCookie(`${UTM_COOKIE_PREFIX}utm-data`, 'utm-data', {
      expires: expireDays,
      sameSite: 'Lax',
    })
  } catch (error) {
    console.error('Error saving UTM to cookies:', error)
  }
}

export const getUtmFromCookies = (): UtmData | null => {
  try {
    const cookies = getCookiesByPrefix(UTM_COOKIE_PREFIX)

    const parameters: Partial<UtmParameters> = {}
    let hasValues = false

    UTM_PARAMETER_KEYS.forEach(key => {
      const value = cookies[key]
      if (value) {
        parameters[key] = value
        hasValues = true
      }
    })

    if (!hasValues) return null

    const timestampStr = cookies.timestamp
    const timestamp = timestampStr ? parseInt(timestampStr) : Date.now()

    return {
      parameters: parameters as UtmParameters,
      timestamp,
      source: 'cookie',
    }
  } catch (error) {
    console.error('Error getting UTM from cookies:', error)
    return null
  }
}

export const saveUtmParameters = async (
  parameters: Partial<UtmParameters>,
  options: StorageOptions = {
    sessionStorage: true,
    localStorage: true,
    indexedDB: true,
    cookies: false,
    expireDays: DEFAULT_COOKIE_EXPIRY_DAYS,
  },
): Promise<void> => {
  const promises: Promise<void>[] = []

  if (options.sessionStorage) {
    promises.push(saveUtmToSessionStorage(parameters))
  }

  if (options.localStorage) {
    promises.push(saveUtmToLocalStorage(parameters))
  }

  if (options.indexedDB) {
    promises.push(saveUtmToIndexedDB(parameters))
  }

  if (options.cookies) {
    promises.push(saveUtmToCookies(parameters, options.expireDays))
  }

  await Promise.allSettled(promises)
}

export const getUtmParameters = async (): Promise<UtmData | null> => {
  try {
    // Try SessionStorage first (most recent)
    const sessionData = getUtmFromSessionStorage()
    if (
      sessionData &&
      Object.values(sessionData.parameters).some(v => v !== null)
    ) {
      return sessionData
    }

    // Try LocalStorage next
    const localData = getUtmFromLocalStorage()
    if (
      localData &&
      Object.values(localData.parameters).some(v => v !== null)
    ) {
      return localData
    }

    // Try IndexedDB
    const indexedDBData = await getUtmFromIndexedDB()
    if (
      indexedDBData &&
      Object.values(indexedDBData.parameters).some(v => v !== null)
    ) {
      return indexedDBData
    }

    // Try Cookies last
    const cookieData = getUtmFromCookies()
    if (
      cookieData &&
      Object.values(cookieData.parameters).some(v => v !== null)
    ) {
      return cookieData
    }

    return null
  } catch (error) {
    console.error('Error getting UTM parameters:', error)
    return null
  }
}

export const ingestUtmParametersFromUrl = async (
  search: string,
  options?: StorageOptions,
): Promise<void> => {
  try {
    const parameters = extractUtmParametersFromUrl(search)

    if (Object.keys(parameters).length > 0) {
      await saveUtmParameters(parameters, options)
    }
  } catch (error) {
    console.error('Error ingesting UTM parameters:', error)
  }
}

/**
 * Clear all UTM data from all storage locations
 */
export const clearAllUtmData = async (): Promise<void> => {
  try {
    // Clear session storage
    sessionStorageUtils.remove(UTM_STORAGE_KEYS.UTM_DATA)

    // Clear local storage
    localStorageUtils.remove(UTM_STORAGE_KEYS.UTM_DATA)

    // Clear IndexedDB
    await deleteFromIndexedDB(UTM_DB_CONFIG, 'utm-data')

    // Clear cookies
    deleteCookiesByPrefix(UTM_COOKIE_PREFIX)
  } catch (error) {
    console.error('Error clearing UTM data:', error)
  }
}

export const saveUtmToDatabase = async (utmData: UtmData): Promise<void> => {
  // TODO: Implement API call to backend
  console.log('TODO: Save UTM data to backend database', utmData)

  /*
  try {
    const response = await fetch('/api/utm/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(utmData),
    })
    
    if (!response.ok) {
      throw new Error('Failed to save UTM data to database')
    }
  } catch (error) {
    console.error('Error saving UTM data to database:', error)
  }
  */
}

export const saveUtmToProfile = async (utmData: UtmData): Promise<void> => {
  // TODO: Implement API call to backend
  console.log('TODO: Save UTM data to profile', utmData)

  getProfileInfo().then(response => {
    console.log('Profile info', response)
  })

  return null
}

/**
 * UTM Parameter Storage Priority
 *
 * The UTM parameters are stored in multiple locations with the following priority order:
 *
 * 1. Session Storage
 *    - Highest priority
 *    - Persists for current browser session only
 *    - Cleared when browser/tab is closed
 *    - Used for immediate tracking of current visit
 *
 * 2. User Profile (if logged in)
 *    - High priority for authenticated users
 *    - Stored server-side with user account
 *    - Enables cross-device attribution
 *    - Helps track conversion path for known users
 *
 * 3. Cookies
 *    - Medium-high priority
 *    - Set with configurable expiration (typically 30-90 days)
 *    - Accessible across site pages
 *    - Good balance of persistence and privacy
 *
 * 4. IndexedDB
 *    - Medium priority
 *    - Client-side database storage
 *    - Larger storage capacity than cookies
 *    - More persistent than session storage
 *    - Used as fallback when cookies are disabled
 *
 * 5. Local Storage
 *    - Lower priority
 *    - Persistent until explicitly cleared
 *    - Used as last resort client-side storage
 *    - No automatic expiration
 *
 * 6. Database with Fingerprint ID
 *    - Lowest priority
 *    - Server-side storage linked to device fingerprint
 *    - Used for anonymous user tracking
 *    - Helps track cross-session behavior
 *    - Privacy considerations must be evaluated
 *
 * This multi-layered approach ensures:
 * - Reliable attribution tracking
 * - Graceful degradation if certain storage methods are unavailable
 * - Balance between user privacy and marketing analytics needs
 * - Cross-session and cross-device user journey tracking where possible
 */
